import { useState } from 'react';
import { AppLayoutWithHeader } from '@/components/layout/AppLayoutWithHeader';
import { ContentContainer } from '@/components/layout/ContentContainer';
import { AlertTriangle } from 'lucide-react';
import { useMembresQuery } from '@/hooks/queries/useMembresQuery';
import { ConformiteScore } from '@/components/conformite/ConformiteScore';
import { ConformiteLegalBanner } from '@/components/conformite/ConformiteLegalBanner';
import { ConformiteTable } from '@/components/conformite/ConformiteTable';
import { ExportConformiteDialog } from '@/components/conformite/ExportConformiteDialog';
import { useEtablissement } from '@/contexts/EtablissementContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

/**
 * Page Conformité — Sprint 3
 * Score de conformité, tableau légal, export multi-format
 */
const Conformite = () => {
  const { data: membres, isLoading, stats, refetch } = useMembresQuery();
  const { etablissement } = useEtablissement();
  const navigate = useNavigate();
  const [exportOpen, setExportOpen] = useState(false);

  const etablissementComplet = !!(
    etablissement?.nom &&
    etablissement?.type_structure &&
    etablissement?.adresse &&
    etablissement?.qualite_responsable &&
    etablissement?.nom_responsable
  );

  return (
    <AppLayoutWithHeader
      title="Conformité 🛡️"
      subtitle="Générez votre preuve légale de conformité au décret n°2026-324"
      layoutMode="default"
      sidebarVisible={true}
    >
      <ContentContainer className="w-full">
        <div className="flex w-full flex-col gap-3.5">
          {/* Bandeau avertissement si infos établissement manquantes */}
          {!etablissementComplet && (
            <div className="w-full rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-white/95 px-5 py-2.5 shadow-[0_10px_28px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] border border-amber-200/80 bg-amber-50">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium leading-5 text-[hsl(var(--app-text))]">
                    Complétez les informations de votre établissement dans les Paramètres pour pouvoir générer votre rapport de conformité.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/settings/etablissement')}
                  className="h-8 shrink-0 px-4 py-1.5 text-[12px]"
                >
                  Compléter maintenant
                </Button>
              </div>
            </div>
          )}

          <ConformiteScore
            stats={stats}
            onRefresh={() => refetch()}
            onExport={() => setExportOpen(true)}
            exportDisabled={!etablissementComplet}
          />

          <section className="app-surface rounded-[20px] p-3.5">
            <div className="flex flex-col gap-3">
              <ConformiteLegalBanner />

              <ConformiteTable
                membres={membres || []}
                loading={isLoading}
              />
            </div>
          </section>
        </div>
      </ContentContainer>

      {/* Modal d'export */}
      <ExportConformiteDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
      />
    </AppLayoutWithHeader>
  );
};

export default Conformite;
